import React, { useMemo } from 'react';
import { Form, FormikProps } from 'formik';
import {
  Product as GqlProduct,
  ProductOptionInput,
  ProductReviewAction,
  ProductStatus,
  ProductType,
} from '@shopify-multiseller/graphql/partner/types';
import { Page } from '../../components/Page/Page';
import { SectionCard } from '../../components/SectionCard';
import { ProductImagesProvider } from '../../components/ProductImage/ProductImagesProvider';
import { ProductStateBadge } from '../../components/StatusBadge/ProductStateBadge';
import { formatDate, formatRelativeTime } from '../../helpers/formatters';
import { ProductTitleDescriptionSection } from './sections/ProductTitleDescriptionSection';
import { ProductPricingInventorySection } from './sections/ProductPricingInventorySection';
import { ProductTypeSection } from './sections/ProductTypeSection';
import { ProductVariantsSection } from './sections/ProductVariantsSection';
import { ProductImagesSection } from './sections/ProductImagesSection';
import { ProductShippingSection } from './sections/ProductShippingSection';
import ProductFeesSection from './sections/ProductFeesSection';
import { Button } from '../../components/primitives';
import { ArchiveProductModal, ContentReviewModal, FullReviewModal, SubmitErrorModal } from './modals';
import { ProductFormEffects } from './ProductFormEffects';
import { generateProductAlerts } from './utils';
import { IProductInitialValuesProps } from './types';
import { ProductFormController } from './useProductForm';

type ContentChangeResponse = {
  title: boolean;
  description: boolean;
  options: boolean;
};

interface ProductFormBodyProps {
  props: FormikProps<IProductInitialValuesProps>;
  form: ProductFormController;
}

/**
 * The one state that answers "can people buy this, and if not whose move is
 * it?" — the live status and the review status resolved into a single chip
 * beside the title.
 */
const renderTitleMeta = (product: GqlProduct) => (
  <ProductStateBadge status={product.status} reviewStatus={product.reviewStatus} />
);

const FactRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border/60 py-2 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-right text-sm font-medium text-foreground">{children}</span>
  </div>
);

/** Sentence case for the state names — "Changes pending", not "CHANGES_PENDING". */
const REVIEW_LABEL: Record<string, string> = {
  APPROVED: 'Approved',
  PENDING: 'Awaiting approval',
  CHANGES_PENDING: 'Changes pending',
  CHANGES_REJECTED: 'Changes requested',
  REJECTED: 'Rejected',
};

/**
 * The four facts the spike states about a product: where it stands, when it
 * was made, when it last changed and where its review has got to.
 *
 * This card previously held only the two dates under the heading "History",
 * so the two states a seller most wants confirmed lived nowhere on the page
 * except as a chip beside the title.
 */
const renderDateBox = (product: GqlProduct) => {
  const reviewLabel = product.reviewStatus ? REVIEW_LABEL[product.reviewStatus] : undefined;
  const reviewPending =
    product.reviewStatus === 'CHANGES_PENDING' || product.reviewStatus === 'PENDING';

  return (
    <SectionCard title="Status" testId="product-history-section">
      <FactRow label="Status">
        {product.status === ProductStatus.Active
          ? 'Active'
          : product.status === ProductStatus.Archived
            ? 'Archived'
            : product.status === ProductStatus.New
              ? 'New'
              : 'Offline'}
      </FactRow>
      <FactRow label="Created">
        {formatDate(product.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}
      </FactRow>
      <FactRow label="Last updated">
        {product.updatedAt === product.createdAt
          ? 'Not updated'
          : formatRelativeTime(product.updatedAt)}
      </FactRow>
      {reviewLabel && (
        <FactRow label="Review">
          <span className={reviewPending ? 'text-amber-700' : undefined}>{reviewLabel}</span>
        </FactRow>
      )}
    </SectionCard>
  );
};

/**
 * Presentation for the product editor: the review modals, the save bar (which
 * routes to the correct review flow), and the section layout. Receives the live
 * Formik `props` and the editor `form` controller from `useProductForm`.
 */
export const ProductFormBody: React.FC<ProductFormBodyProps> = ({ props, form }) => {
  const hasErrors = Object.keys(props.errors).length > 0;
  const hasSubmitted = props.submitCount > 0;

  const hasChanged = (
    a: ProductOptionInput[] | null | undefined,
    b: ProductOptionInput[] | null | undefined,
  ) => JSON.stringify(a) !== JSON.stringify(b);

  const contentModified = useMemo<ContentChangeResponse | false>(() => {
    const titleChanged = props.initialValues.title !== props.values.title;
    const descriptionChanged = props.initialValues.description !== props.values.description;
    const optionsChanged = hasChanged(props.initialValues.options, props.values.options);

    if (titleChanged || descriptionChanged || optionsChanged) {
      return {
        title: titleChanged,
        description: descriptionChanged,
        options: optionsChanged,
      };
    }
    return false;
  }, [props.initialValues, props.values.title, props.values.description, props.values.options]);

  return (
    <ProductImagesProvider product={form.product || null}>
      <>
        <ProductFormEffects props={props} trackQuantity={form.trackQuantity} />
        <FullReviewModal
          props={props}
          showFullReviewModal={form.showFullReviewModal}
          setShowFullReviewModal={form.setShowFullReviewModal}
          disableProductUntilChangesAccepted={form.disableProductUntilChangesAccepted}
          setDisableProductUntilChangesAccepted={form.setDisableProductUntilChangesAccepted}
        />
        <ContentReviewModal
          props={props}
          contentModified={contentModified}
          showContentReviewModal={form.showContentReviewModal}
          setShowContentReviewModal={form.setShowContentReviewModal}
          disableProductUntilChangesAccepted={form.disableProductUntilChangesAccepted}
          setDisableProductUntilChangesAccepted={form.setDisableProductUntilChangesAccepted}
        />
        <SubmitErrorModal
          props={props}
          showSubmitErrorModal={form.showSubmitErrorModal}
          setShowSubmitErrorModal={form.setShowSubmitErrorModal}
        />
        <ArchiveProductModal
          title={props.initialValues.title || 'This product'}
          open={form.showArchiveModal}
          loading={form.archiving}
          onClose={() => form.setShowArchiveModal(false)}
          onConfirm={form.handleArchive}
        />

        <Form onChange={props.handleChange} onBlur={props.handleBlur} onSubmit={props.handleSubmit}>
          <Page
            // An existing product without a title is untitled, not new: the
            // page was calling itself "Add a product" while showing that
            // product's breadcrumb, state chip and history.
            title={
              form.isAddProduct
                ? 'Add a product'
                : props.initialValues.title || 'Untitled product'
            }
            breadcrumbs={
              form.isAddProduct
                ? [
                    {
                      content: 'Products',
                      url: '/products',
                    },
                    {
                      content: 'Add product',
                      url: '/products/add',
                      current: true,
                    },
                  ]
                : form.product
                  ? [
                      { content: 'Products', url: '/products' },
                      { content: form.product.idAlias, url: `/products/${form.product.id}`, current: true },
                    ]
                  : []
            }
            titleMeta={form.product ? renderTitleMeta(form.product) : undefined}
            // Beside the title, as the design draws it. Not offered on a
            // product being added (there is nothing there yet to take down) or
            // on one already archived.
            actions={
              form.product && form.product.status !== ProductStatus.Archived ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="product-archive-button"
                  onClick={() => form.setShowArchiveModal(true)}
                >
                  Archive
                </Button>
              ) : undefined
            }
            alerts={form.product ? generateProductAlerts(form.product) : []}
            saveBar={{
              message:
                hasErrors && hasSubmitted ? 'Validation errors need to be resolved' : 'Unsaved changes',
              messageStatus: hasErrors && hasSubmitted ? 'error' : 'info',
              show: props.dirty,
              discardAction: {
                onAction: props.handleReset,
                disabled: props.isSubmitting,
                loading: props.isSubmitting,
              },
              saveAction: {
                onAction: () => {
                  if (form.isAddProduct) {
                    props.handleSubmit();
                  } else {
                    if (form.product) {
                      if (form.settings.productReview?.reviewAction === ProductReviewAction.ContentReview) {
                        if (contentModified) {
                          form.setShowContentReviewModal(true);
                        } else {
                          props.handleSubmit();
                        }
                      } else if (form.settings.productReview?.reviewAction === ProductReviewAction.FullReview) {
                        form.setShowFullReviewModal(true);
                      } else {
                        props.handleSubmit();
                      }
                    }
                  }
                },
                disabled: props.isSubmitting || (hasErrors && hasSubmitted),
                loading: props.isSubmitting,
              },
            }}
          >
            <div className="flex flex-wrap items-start justify-center gap-5">
              <div className="min-w-[51%] flex-[2_2_30rem] space-y-5">
                <ProductTitleDescriptionSection disabled={form.disabled} />
                <ProductImagesSection disabled={form.disabled} product={form.product || undefined} />

                {/* Its own panel, above pricing: the type decides whether the
                    product ships and whether stock is tracked, so everything
                    below follows from it. */}
                <ProductTypeSection disabled={form.disabled} setTrackQuantity={form.setTrackQuantity} />
                <ProductPricingInventorySection
                  disabled={form.disabled}
                  index={0}
                  setTrackQuantity={form.setTrackQuantity}
                  variants={form.variants || []}
                />
                <ProductShippingSection disabled={form.disabled} />
                <ProductVariantsSection
                  disabled={form.disabled}
                  variants={form.variants || []}
                  trackQuantity={form.trackQuantity}
                  isPhysicalProduct={props.values.productType === ProductType.Physical}
                />
              </div>
              <div className="min-w-0 flex-[1_1_15rem] space-y-5">
                {/* `.length`, not just the array: an empty `variants` passed
                    this guard and handed ProductFeesSection `undefined`, which
                    threw on `variant.price` and replaced the whole editor with
                    a router error page. */}
                {/* The estimate shows on a product with variations too. It was
                    hidden there because one figure beside several prices says
                    nothing about which — so the figure names its variation and
                    says that it varies, which is what the design draws. Hiding
                    it left the seller of a multi-variation product with no
                    payout estimate at all, on the screen where they set the
                    price. */}
                {props.values.variants?.length > 0 && form.product?.status !== ProductStatus.Archived && (
                  <ProductFeesSection
                    variant={props.values.variants[0]}
                    productSettings={form.product?.settings}
                  />
                )}
                {form.product && renderDateBox(form.product)}
              </div>
            </div>
          </Page>
        </Form>
      </>
    </ProductImagesProvider>
  );
};
